import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Globe,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ShieldCheck,
  Code2,
  Play,
  Clock,
  Layers,
  Monitor,
  FileCode2,
  ChevronRight,
} from 'lucide-react';
import api from '../../services/api';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface ProcessStages {
  scanned_at?: string;
  report_generated_at?: string;
  suite_generated_at?: string;
  playwright_executed_at?: string;
  [key: string]: string | undefined;
}

interface ExecutionResults {
  executed_at?: string;
  test_count: number;
  passed: number;
  failed: number;
  skipped: number;
  pass_rate: number;
  duration_ms: number;
  failed_tests: string[];
}

interface Scan {
  id: string;
  url: string;
  title: string;
  status: string;
  browser: string;
  viewport_w: number;
  viewport_h: number;
  skope_version?: string;
  total_elements: number;
  critical_count: number;
  alta_count: number;
  media_count: number;
  baja_count: number;
  compliance_pct: number;
  process_stages?: ProcessStages;
  execution_results?: ExecutionResults;
  generated_page_ts?: string;
  generated_spec_ts?: string;
  created_at: string;
  updated_at: string;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

const statusLabel: Record<string, { label: string; color: string; bg: string }> = {
  scanned:   { label: 'Scanned',   color: 'text-blue-400',    bg: 'bg-blue-500/10 border-blue-500/30' },
  generated: { label: 'Generated', color: 'text-purple-400',  bg: 'bg-purple-500/10 border-purple-500/30' },
  executed:  { label: 'Executed',  color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/30' },
  k6_ready:  { label: 'K6 Ready', color: 'text-yellow-400',  bg: 'bg-yellow-500/10 border-yellow-500/30' },
  error:     { label: 'Error',     color: 'text-red-400',     bg: 'bg-red-500/10 border-red-500/30' },
};

function fmtDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-CL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function complianceColor(pct: number): string {
  if (pct >= 80) return 'text-emerald-400';
  if (pct >= 50) return 'text-yellow-400';
  return 'text-red-400';
}

function complianceBg(pct: number): string {
  if (pct >= 80) return 'bg-emerald-500';
  if (pct >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
}

// ─── Component ─────────────────────────────────────────────────────────────────

const ScanDetail: React.FC = () => {
  const { scanId } = useParams<{ scanId: string }>();
  const navigate = useNavigate();
  const [scan, setScan] = useState<Scan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'playwright' | 'spec'>('overview');
  const [codeTab, setCodeTab] = useState<'page' | 'spec'>('page');

  useEffect(() => {
    if (scanId) loadScan(scanId);
  }, [scanId]);

  const loadScan = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getScan(id);
      setScan(data);
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? 'Error al cargar el scan');
    } finally {
      setLoading(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !scan) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors">
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
          <XCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <p className="text-red-300 font-medium">{error ?? 'Scan no encontrado'}</p>
          <p className="text-slate-500 text-sm mt-1">ID: {scanId}</p>
        </div>
      </div>
    );
  }

  const st = statusLabel[scan.status] ?? { label: scan.status, color: 'text-slate-400', bg: 'bg-slate-500/10 border-slate-500/30' };
  const exec = scan.execution_results;
  const stages = scan.process_stages ?? {};

  // ── Timeline stages ────────────────────────────────────────────────────────
  const timeline = [
    { key: 'scanned_at',            label: 'Analizado',        icon: Globe },
    { key: 'report_generated_at',   label: 'Reporte HTML',     icon: FileCode2 },
    { key: 'suite_generated_at',    label: 'Suite generada',   icon: Code2 },
    { key: 'playwright_executed_at',label: 'Tests ejecutados', icon: Play },
  ];

  return (
    <div className="space-y-6 max-w-5xl">

      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>

      {/* Header card */}
      <div className="bg-surface-dark border border-slate-800 rounded-xl p-5">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
            <ShieldCheck className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h1 className="text-xl font-bold text-white truncate">{scan.title || scan.url}</h1>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${st.color} ${st.bg}`}>
                {st.label}
              </span>
            </div>
            <a
              href={scan.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-slate-400 hover:text-primary flex items-center gap-1 w-fit"
            >
              <Globe className="w-3.5 h-3.5" />
              {scan.url}
            </a>
            <div className="flex flex-wrap gap-4 mt-2 text-xs text-slate-500">
              <span className="flex items-center gap-1"><Monitor className="w-3.5 h-3.5" /> {scan.browser} · {scan.viewport_w}×{scan.viewport_h}</span>
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {fmtDate(scan.created_at)}</span>
              {scan.skope_version && <span className="flex items-center gap-1"><Layers className="w-3.5 h-3.5" /> v{scan.skope_version}</span>}
            </div>
          </div>
          {/* Compliance badge */}
          <div className="text-center shrink-0">
            <div className={`text-3xl font-black ${complianceColor(scan.compliance_pct)}`}>
              {scan.compliance_pct.toFixed(1)}%
            </div>
            <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Compliance</div>
          </div>
        </div>
      </div>

      {/* Metrics row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Total',    value: scan.total_elements,  color: 'text-white',        dot: 'bg-slate-400' },
          { label: 'Crítico',  value: scan.critical_count,  color: 'text-red-400',      dot: 'bg-red-500' },
          { label: 'Alta',     value: scan.alta_count,      color: 'text-orange-400',   dot: 'bg-orange-500' },
          { label: 'Media',    value: scan.media_count,     color: 'text-yellow-400',   dot: 'bg-yellow-500' },
          { label: 'Baja',     value: scan.baja_count,      color: 'text-emerald-400',  dot: 'bg-emerald-500' },
        ].map(m => (
          <div key={m.label} className="bg-surface-dark border border-slate-800 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <div className={`w-2 h-2 rounded-full ${m.dot}`} />
              <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider">{m.label}</span>
            </div>
            <div className={`text-2xl font-black ${m.color}`}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* Compliance bar */}
      <div className="bg-surface-dark border border-slate-800 rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-400 font-medium">Accesibilidad ARIA/WCAG</span>
          <span className={`text-sm font-bold ${complianceColor(scan.compliance_pct)}`}>{scan.compliance_pct.toFixed(1)}%</span>
        </div>
        <div className="w-full bg-slate-800 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-700 ${complianceBg(scan.compliance_pct)}`}
            style={{ width: `${Math.min(scan.compliance_pct, 100)}%` }}
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-900 border border-slate-800 rounded-lg p-1 w-fit">
        {([
          { key: 'overview',   label: 'Resumen' },
          { key: 'playwright', label: 'Resultados Playwright', hidden: !exec },
          { key: 'spec',       label: 'Código generado',       hidden: !scan.generated_page_ts && !scan.generated_spec_ts },
        ] as { key: string; label: string; hidden?: boolean }[])
          .filter(t => !t.hidden)
          .map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key as typeof activeTab)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === t.key
                  ? 'bg-primary text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {t.label}
            </button>
          ))
        }
      </div>

      {/* Tab: Overview — timeline */}
      {activeTab === 'overview' && (
        <div className="bg-surface-dark border border-slate-800 rounded-xl p-5 space-y-4">
          <h2 className="text-base font-semibold text-white mb-3">Pipeline de trazabilidad</h2>
          <div className="space-y-3">
            {timeline.map((step, idx) => {
              const done = !!stages[step.key];
              const Icon = step.icon;
              return (
                <div key={step.key} className="flex items-center gap-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                    done ? 'bg-emerald-500/20 border border-emerald-500/50' : 'bg-slate-800 border border-slate-700'
                  }`}>
                    <Icon className={`w-4 h-4 ${done ? 'text-emerald-400' : 'text-slate-600'}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${done ? 'text-white' : 'text-slate-600'}`}>{step.label}</span>
                      {done && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
                    </div>
                    {done && <span className="text-xs text-slate-500">{fmtDate(stages[step.key])}</span>}
                  </div>
                  {idx < timeline.length - 1 && (
                    <ChevronRight className={`w-4 h-4 ${done ? 'text-emerald-500/50' : 'text-slate-700'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tab: Playwright results */}
      {activeTab === 'playwright' && exec && (
        <div className="space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Tests',     value: exec.test_count,                        color: 'text-white' },
              { label: 'Passed',    value: exec.passed,                            color: 'text-emerald-400' },
              { label: 'Failed',    value: exec.failed,                            color: exec.failed > 0 ? 'text-red-400' : 'text-slate-500' },
              { label: 'Pass Rate', value: `${exec.pass_rate}%`,                   color: exec.pass_rate === 100 ? 'text-emerald-400' : exec.pass_rate >= 80 ? 'text-yellow-400' : 'text-red-400' },
            ].map(m => (
              <div key={m.label} className="bg-surface-dark border border-slate-800 rounded-xl p-4 text-center">
                <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-1">{m.label}</div>
                <div className={`text-2xl font-black ${m.color}`}>{m.value}</div>
              </div>
            ))}
          </div>

          {/* Duration */}
          <div className="bg-surface-dark border border-slate-800 rounded-xl p-4 flex items-center gap-3">
            <Clock className="w-5 h-5 text-slate-500" />
            <span className="text-sm text-slate-400">Duración total:</span>
            <span className="text-white font-semibold">{(exec.duration_ms / 1000).toFixed(1)}s</span>
            <span className="text-xs text-slate-600 ml-auto">{fmtDate(exec.executed_at)}</span>
          </div>

          {/* Failed tests */}
          {exec.failed_tests.length > 0 && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-sm font-semibold text-red-300">Tests fallidos</span>
              </div>
              <ul className="space-y-1.5">
                {exec.failed_tests.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <XCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <span className="text-red-200 font-mono text-xs">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {exec.failed === 0 && (
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              <span className="text-emerald-300 font-medium">Todos los tests pasaron correctamente</span>
            </div>
          )}
        </div>
      )}

      {/* Tab: Generated code */}
      {activeTab === 'spec' && (
        <div className="space-y-3">
          {/* Sub-tabs */}
          <div className="flex gap-1 bg-slate-900 border border-slate-800 rounded-lg p-1 w-fit">
            {scan.generated_page_ts && (
              <button
                onClick={() => setCodeTab('page')}
                className={`px-3 py-1 rounded text-xs font-mono font-medium transition-colors ${
                  codeTab === 'page' ? 'bg-slate-700 text-white' : 'text-slate-500 hover:text-white'
                }`}
              >
                .page.ts
              </button>
            )}
            {scan.generated_spec_ts && (
              <button
                onClick={() => setCodeTab('spec')}
                className={`px-3 py-1 rounded text-xs font-mono font-medium transition-colors ${
                  codeTab === 'spec' ? 'bg-slate-700 text-white' : 'text-slate-500 hover:text-white'
                }`}
              >
                .spec.ts
              </button>
            )}
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-800 bg-slate-900/80">
              <Code2 className="w-4 h-4 text-primary" />
              <span className="text-xs font-mono text-slate-400">
                {codeTab === 'page' ? 'Page Object' : 'Spec / Tests'}
              </span>
            </div>
            <pre className="p-4 text-xs text-slate-300 font-mono overflow-x-auto leading-relaxed max-h-[500px] overflow-y-auto">
              {codeTab === 'page' ? scan.generated_page_ts : scan.generated_spec_ts}
            </pre>
          </div>
        </div>
      )}

      {/* Scan ID footer */}
      <div className="text-xs text-slate-600 font-mono border-t border-slate-800 pt-3">
        Scan ID: {scan.id}
      </div>
    </div>
  );
};

export default ScanDetail;
